import { faLightbulb } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import * as style from './style.module.css';

const MARGIN = 8;
const GAP = 8;

const Tooltip = ({ children, text, position = 'right', popupIcon = true, display = true }) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: undefined, right: undefined });
    const [arrowOffset, setArrowOffset] = useState('50%');
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);

    const handleMouseEnter = () => {
        if (containerRef.current) {
            setCoords(containerRef.current.getBoundingClientRect());
        }
        setVisible(true);
    };

    const handleMouseLeave = () => {
        setVisible(false);
    };

    useLayoutEffect(() => {
        if (!coords || !tooltipRef.current) return;
        const tooltipEl = tooltipRef.current;

        if (position === 'top') {
            const tooltipHeight = tooltipEl.offsetHeight;
            const tooltipWidth = tooltipEl.offsetWidth;
            const rawTop = coords.top + window.scrollY - tooltipHeight - GAP;
            const rawLeft = coords.left + window.scrollX + coords.width / 2 - tooltipWidth / 2;
            const clampedLeft = Math.min(Math.max(rawLeft, MARGIN), window.innerWidth - tooltipWidth - MARGIN);
            setTooltipPos({ top: rawTop, left: clampedLeft, right: undefined });
            const triggerCenterX = coords.left + window.scrollX + coords.width / 2;
            setArrowOffset(triggerCenterX - clampedLeft);
        } else {
            const left = position === 'right' ? coords.right + window.scrollX : undefined;
            const right = position === 'left' ? window.innerWidth - coords.left + window.scrollX : undefined;
            // The width of the tooltip, hence its height, depends on where it starts: until it is placed beside its
            // trigger, the stylesheet starts it at the edge of the window, where its text wraps at every word.
            tooltipEl.style.left = left === undefined ? '' : `${left}px`;
            tooltipEl.style.right = right === undefined ? '' : `${right}px`;
            const tooltipHeight = tooltipEl.offsetHeight;
            const spaceS = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--space-s'), 10) || 16;
            const ARROW_TOP_OFFSET = spaceS + 10;
            const triggerCenterY = coords.top + window.scrollY + coords.height / 2;
            const minTop = window.scrollY + MARGIN;
            const maxTop = window.scrollY + window.innerHeight - tooltipHeight - MARGIN;
            const desiredTop = triggerCenterY - ARROW_TOP_OFFSET;
            const clampedTop = Math.min(Math.max(desiredTop, minTop), maxTop);
            setTooltipPos({ top: clampedTop, left, right });
            setArrowOffset(triggerCenterY - clampedTop);
        }
    }, [coords, position]);

    if (!display) return children;

    const positionClass = position === 'top' ? style.tooltipTop : position === 'left' ? style.tooltipLeft : style.tooltipRight;

    return (
        <div
            ref={containerRef}
            className={style.container}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            // Keyboard users reach the tooltip by focusing a trigger inside the container.
            onFocus={handleMouseEnter}
            onBlur={handleMouseLeave}
        >
            {children}

            {visible &&
                coords &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        className={`${style.tooltip} ${positionClass}`}
                        style={{
                            top: tooltipPos.top,
                            left: tooltipPos.left,
                            right: tooltipPos.right,
                            '--arrow-offset': typeof arrowOffset === 'number' ? `${arrowOffset}px` : arrowOffset,
                        }}
                    >
                        {popupIcon && (
                            <div className={style.iconBackGround}>
                                <FontAwesomeIcon icon={faLightbulb} className={style.icon} />
                            </div>
                        )}
                        <span className={style.text}>{text}</span>
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default Tooltip;
